import { Toggle } from '@/components/Elements';
import { AnalyzeLayout } from '@/features/analyze/components/AnalyzeLayout';
import { HeatmapSearchResult } from '@/features/analyze/components/HeatmapSearchResult';
import { HeatmapView } from '@/features/analyze/components/HeatmapView';
import { ResultNotFond } from '@/features/analyze/components/ResultNotFond';
import { TransitionRangeGapSelect } from '@/features/analyze/components/TransitionRangeGapSelect';
import { useAnalyzeTransition } from '@/features/analyze/hooks/useAnalyzeTransition';
import { useTransitionSelectedRangeResult } from '@/features/analyze/hooks/useTransitionSelectedRangeResult';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type TransitionData = {
  values: number[][];
  xLabels: string[];
  yLabels: string[];
};

const filterTransitionData = (
  transitionData: TransitionData | null | undefined,
  showMultiNational: boolean
) => {
  if (transitionData == null || showMultiNational) return transitionData;

  const multinationalIndex = transitionData.yLabels.findIndex(
    (label) => label === 'Multinational'
  );

  if (multinationalIndex === -1) {
    return transitionData;
  }

  return {
    values: transitionData.values.filter(
      (_, index) => index !== multinationalIndex
    ),
    xLabels: transitionData.xLabels,
    yLabels: transitionData.yLabels.filter(
      (_, index) => index !== multinationalIndex
    ),
  };
};

export const Transition = () => {
  const { t } = useTranslation();
  const [showMultiNational, setShowMultiNational] = useState(false);
  const { transitionData, isLoading } = useAnalyzeTransition();
  const { docs, selectedRangeCondition, setSelectedRangeCondition, pageInfo } =
    useTransitionSelectedRangeResult();

  const filteredTransitionData = useMemo(
    () => filterTransitionData(transitionData, showMultiNational),
    [transitionData, showMultiNational]
  );

  if (!isLoading && !transitionData) {
    return (
      <AnalyzeLayout>
        <ResultNotFond />
      </AnalyzeLayout>
    );
  }

  return (
    <AnalyzeLayout>
      <div className="w-full p-3">
        <div className="rounded-md bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex flex-col gap-2">
              <h3>{t('国別言及動向')}</h3>
              <div className="flex items-center gap-x-2 text-xs">
                <span className="text-xs">{t('Multinational 表示')}:</span>
                <Toggle
                  size="sm"
                  checked={showMultiNational}
                  onChange={setShowMultiNational}
                />
              </div>
            </div>
            <div className="flex items-center gap-x-3">
              <span className="text-xs">{t('表示間隔')}:</span>
              <TransitionRangeGapSelect />
            </div>
          </div>

          <HeatmapView
            data={filteredTransitionData}
            setSelectedRangeCondition={setSelectedRangeCondition}
          />
        </div>

        <div className="m-auto mt-3 flex w-full flex-col items-center gap-y-4 rounded-md bg-white p-4">
          <HeatmapSearchResult
            docs={docs}
            pageInfo={pageInfo}
            selectedRangeCondition={selectedRangeCondition}
            setSelectedRangeCondition={setSelectedRangeCondition}
          />
        </div>
      </div>
    </AnalyzeLayout>
  );
};
